import {
  InvoicePaymentMentionLocales,
  ParticularMentionLocale,
  QuotePaymentMentionLocales,
} from '@shinetools/invoicing-renderer';

import { InvoicingSpecialMentions as InvoicingSpecialMentionsNullable } from '../../../pages/hooks/useInvoicingSpecialMentionsQuery';

type InvoicingSpecialMentions = NonNullable<InvoicingSpecialMentionsNullable>;

type MentionsUnion =
  | ParticularMentionLocale
  | QuotePaymentMentionLocales
  | InvoicePaymentMentionLocales;

const constructLocalizedObject =
  <T extends MentionsUnion>(locales: T[]) =>
  (item: T['type']): T => {
    const localeMatch = locales.find((locale) => locale.type === item);
    if (!localeMatch) {
      throw new Error(`No locales match for ${item}`);
    }
    return localeMatch;
  };

const mapToLocalizedObjects = <T extends MentionsUnion>(
  items: T['type'][],
  locales: T[],
): T[] => {
  return items.map(constructLocalizedObject(locales));
};

type InvoiceSource = {
  particularMentions: ParticularMentionLocale['type'][];
  paymentMentions: InvoicePaymentMentionLocales['type'][];
};

export const getLocalizedMentionsFromInvoice = (
  invoice: InvoiceSource,
  invoicingSpecialMentions: InvoicingSpecialMentions,
) => {
  const particularMentions = mapToLocalizedObjects<ParticularMentionLocale>(
    invoice.particularMentions,
    invoicingSpecialMentions.particularMentions,
  );
  const paymentMentions = mapToLocalizedObjects<InvoicePaymentMentionLocales>(
    invoice.paymentMentions,
    invoicingSpecialMentions.invoicePaymentMentions,
  );
  return {
    particularMentions,
    paymentMentions,
  };
};

type QuoteSource = {
  particularMentions: ParticularMentionLocale['type'][];
  paymentMentions: QuotePaymentMentionLocales['type'][];
};

export const getLocalizedMentionsFromQuote = (
  quote: QuoteSource,
  invoicingSpecialMentions: InvoicingSpecialMentions,
) => {
  const particularMentions = mapToLocalizedObjects<ParticularMentionLocale>(
    quote.particularMentions,
    invoicingSpecialMentions.particularMentions,
  );
  const paymentMentions = mapToLocalizedObjects<QuotePaymentMentionLocales>(
    quote.paymentMentions,
    invoicingSpecialMentions.quotePaymentMentions,
  );
  return {
    particularMentions,
    paymentMentions,
  };
};
